'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { PDFDocument, StandardFonts, degrees, rgb } from 'pdf-lib';
import {
  createCleanorExportFileName,
  createPdfPreviewUrl,
  formatFileSize,
  isPdfFile,
  openPdfDocument,
  triggerBrowserDownload,
} from '@/lib/browser-pdf-tools';
import { consumeToolUsage, formatToolCreditSuccess } from '@/lib/browser-tool-usage';

type WatermarkSource = {
  id: string;
  file: File;
  name: string;
  size: number;
  pageCount: number;
  previewUrl: string | null;
};

type WatermarkPosition = 'center' | 'header' | 'footer' | 'bottom-right';

const opacityOptions = [
  { value: 0.12, label: 'Light' },
  { value: 0.18, label: 'Balanced' },
  { value: 0.26, label: 'Stronger' },
] as const;

const scaleOptions = [
  { value: 0.08, label: 'Subtle' },
  { value: 0.11, label: 'Balanced' },
  { value: 0.14, label: 'Large' },
] as const;

async function readSource(file: File): Promise<WatermarkSource> {
  const document = await openPdfDocument(new Uint8Array(await file.arrayBuffer()));

  try {
    return {
      id: crypto.randomUUID(),
      file,
      name: file.name,
      size: file.size,
      pageCount: document.numPages,
      previewUrl: await createPdfPreviewUrl(document),
    };
  } catch {
    throw new Error(`"${file.name}" could not be opened. Use a standard unlocked PDF file.`);
  } finally {
    document.cleanup?.();
    await document.destroy?.();
  }
}

function createExportFileName(position: WatermarkPosition, pageCount: number) {
  const pageLabel = pageCount === 1 ? '1-page' : `${pageCount}-pages`;
  return createCleanorExportFileName('watermark-pdf', [position, pageLabel], 'pdf');
}

export default function BrowserWatermarkPdf() {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const downloadUrlRef = useRef<string | null>(null);
  const [source, setSource] = useState<WatermarkSource | null>(null);
  const [watermarkText, setWatermarkText] = useState('Confidential');
  const [position, setPosition] = useState<WatermarkPosition>('center');
  const [opacity, setOpacity] = useState<number>(opacityOptions[1].value);
  const [scale, setScale] = useState<number>(scaleOptions[1].value);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState<number | null>(null);

  const previewLabel = useMemo(() => {
    switch (position) {
      case 'header':
        return 'Top center watermark';
      case 'footer':
        return 'Bottom center watermark';
      case 'bottom-right':
        return 'Bottom right watermark';
      default:
        return 'Centered diagonal watermark';
    }
  }, [position]);

  useEffect(() => {
    return () => {
      if (source?.previewUrl) {
        URL.revokeObjectURL(source.previewUrl);
      }

      if (downloadUrlRef.current) {
        URL.revokeObjectURL(downloadUrlRef.current);
      }
    };
  }, [source]);

  function resetExportState() {
    if (downloadUrlRef.current) {
      URL.revokeObjectURL(downloadUrlRef.current);
    }

    downloadUrlRef.current = null;
    setResultSize(null);
  }

  function replaceSource(nextSource: WatermarkSource | null) {
    setSource((current) => {
      if (current?.previewUrl) {
        URL.revokeObjectURL(current.previewUrl);
      }

      return nextSource;
    });
  }

  async function handleFileSelection(file: File | null) {
    setErrorMessage(null);
    setCreditMessage(null);
    resetExportState();

    if (!file) {
      replaceSource(null);
      return;
    }

    if (!isPdfFile(file)) {
      replaceSource(null);
      setErrorMessage('Choose one PDF file to watermark.');
      return;
    }

    try {
      replaceSource(await readSource(file));
    } catch (error) {
      replaceSource(null);
      setErrorMessage(error instanceof Error ? error.message : 'This PDF could not be opened.');
    }
  }

  function clearSource() {
    replaceSource(null);
    setErrorMessage(null);
    setCreditMessage(null);
    resetExportState();

    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }

  async function saveWatermarkedPdf() {
    if (!source) {
      setErrorMessage('Choose a PDF first.');
      return;
    }

    if (!watermarkText.trim()) {
      setErrorMessage('Enter the watermark text first.');
      return;
    }

    setIsSaving(true);
    setErrorMessage(null);
    setCreditMessage(null);

    try {
      const credits = await consumeToolUsage(
        'watermark-pdf',
        'Credits are not available for another PDF watermark right now.',
      );

      const document = await PDFDocument.load(await source.file.arrayBuffer());
      const font = await document.embedFont(StandardFonts.HelveticaBold);

      for (const page of document.getPages()) {
        const { width, height } = page.getSize();
        const fontSize = Math.max(18, Math.min(96, width * scale));
        const textWidth = font.widthOfTextAtSize(watermarkText, fontSize);
        const textHeight = font.heightAtSize(fontSize);

        switch (position) {
          case 'header':
            page.drawText(watermarkText, {
              x: Math.max(24, (width - textWidth) / 2),
              y: height - textHeight - 28,
              size: fontSize,
              font,
              color: rgb(0.15, 0.18, 0.24),
              opacity,
            });
            break;
          case 'footer':
            page.drawText(watermarkText, {
              x: Math.max(24, (width - textWidth) / 2),
              y: 24,
              size: fontSize,
              font,
              color: rgb(0.15, 0.18, 0.24),
              opacity,
            });
            break;
          case 'bottom-right':
            page.drawText(watermarkText, {
              x: Math.max(24, width - textWidth - 26),
              y: 24,
              size: fontSize,
              font,
              color: rgb(0.15, 0.18, 0.24),
              opacity,
            });
            break;
          default:
            page.drawText(watermarkText, {
              x: (width - textWidth) / 2,
              y: (height - textHeight) / 2,
              size: fontSize,
              font,
              color: rgb(0.15, 0.18, 0.24),
              opacity,
              rotate: degrees(-32),
            });
            break;
        }
      }

      const bytes = await document.save();
      const blob = new Blob([bytes.buffer as ArrayBuffer], { type: 'application/pdf' });
      const nextUrl = URL.createObjectURL(blob);
      downloadUrlRef.current = nextUrl;
      setResultSize(blob.size);
      triggerBrowserDownload(nextUrl, createExportFileName(position, source.pageCount));
      setCreditMessage(formatToolCreditSuccess('Watermark export', credits));
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'This PDF could not be watermarked.',
      );
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <article className="tool-live-card tool-pdf-card">
      <div className="tool-live-header">
        <div>
          <h2>Add a text watermark to one PDF</h2>
          <p>
            Stamp a PDF with a simple text watermark in the browser, keep the original local, and
            download the updated file right after export.
          </p>
        </div>
        <span className="tool-live-badge">Browser-first</span>
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <button
            className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              void handleFileSelection(event.dataTransfer.files?.[0] ?? null);
            }}
            type="button"
          >
            <div>
              <strong>
                {source ? 'PDF ready for watermarking' : 'Choose one PDF or drop it here'}
              </strong>
              <span>
                {source
                  ? `${source.pageCount} pages • ${formatFileSize(source.size)}`
                  : 'Works best with standard unlocked PDF files'}
              </span>
            </div>
          </button>
          <input
            ref={inputRef}
            accept="application/pdf,.pdf"
            className="tool-live-file-input"
            onChange={(event) => void handleFileSelection(event.target.files?.[0] ?? null)}
            type="file"
          />

          <div className="tool-live-controls tool-pdf-settings">
            <label className="tool-live-field">
              <span>Watermark text</span>
              <input
                maxLength={64}
                onChange={(event) => setWatermarkText(event.target.value)}
                placeholder="Confidential"
                value={watermarkText}
              />
            </label>

            <label className="tool-live-field">
              <span>Placement</span>
              <select
                onChange={(event) => setPosition(event.target.value as WatermarkPosition)}
                value={position}
              >
                <option value="center">Centered diagonal</option>
                <option value="header">Top center</option>
                <option value="footer">Bottom center</option>
                <option value="bottom-right">Bottom right</option>
              </select>
            </label>

            <label className="tool-live-field">
              <span>Opacity</span>
              <select
                onChange={(event) => setOpacity(Number(event.target.value))}
                value={String(opacity)}
              >
                {opacityOptions.map((option) => (
                  <option key={option.label} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="tool-live-field">
              <span>Scale</span>
              <select
                onChange={(event) => setScale(Number(event.target.value))}
                value={String(scale)}
              >
                {scaleOptions.map((option) => (
                  <option key={option.label} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="hero-actions tool-live-actions">
            <button
              className="button"
              disabled={!source || isSaving}
              onClick={() => void saveWatermarkedPdf()}
              type="button"
            >
              {isSaving ? 'Saving PDF…' : 'Watermark PDF'}
            </button>
            {source ? (
              <button className="button button-ghost" onClick={clearSource} type="button">
                Clear file
              </button>
            ) : null}
          </div>

          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <h3>Simple PDF watermark</h3>
          <div className="tool-mockup-chip-list">
            <span>PDF → PDF</span>
            <span>Text stamp</span>
            <span>{previewLabel}</span>
            <span>Local export</span>
          </div>
          <p>
            Use this for drafts, client copies, internal sharing, and lightweight branded exports.
          </p>
          <div className="tool-live-panel-note">
            <strong>Best browser support</strong>
            <span>
              This tool adds text watermarks to standard unlocked PDFs without sending them to a
              server.
            </span>
          </div>
          {source ? (
            <div className="tool-live-stats">
              <div>
                <strong>Pages</strong>
                <span>{source.pageCount}</span>
              </div>
              <div>
                <strong>Source size</strong>
                <span>{formatFileSize(source.size)}</span>
              </div>
              {resultSize ? (
                <div>
                  <strong>Last export</strong>
                  <span>{formatFileSize(resultSize)}</span>
                </div>
              ) : null}
              <div>
                <strong>Placement</strong>
                <span>{previewLabel}</span>
              </div>
              <div>
                <strong>Privacy</strong>
                <span>Processed in your browser</span>
              </div>
            </div>
          ) : null}
        </aside>
      </div>

      {source?.previewUrl ? (
        <div className="tool-live-preview">
          <h3>Source preview</h3>
          <div className="tool-live-preview-frame">
            <img alt="" src={source.previewUrl} />
          </div>
        </div>
      ) : null}
    </article>
  );
}
